import { useState } from "react";
import { mutate } from "swr";
import { adminChangeRequestsService } from "../../api_client";
import type {
  ChangeRequestDto,
  ReviewChangeRequestDto,
} from "../../../api";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";

function extractErrorMessage(error: unknown, fallback: string): string {
  const typed = error as { response?: { data?: { message?: string | string[] } }; message?: string };
  const raw = typed?.response?.data?.message ?? typed?.message;
  // class-validator emits one message per failed decorator. Joining them
  // with ", " produced misleading toasts like
  //   "reviewNote must be a string, reviewNote must be shorter than or equal to 1000 characters"
  // when the real cause was a missing note (client repro: "1000 chars
  // or less - even when 5 entered"). Surface the first message instead —
  // the BE DTO is now responsible for emitting the single most relevant
  // reason.
  if (Array.isArray(raw)) return raw[0] ?? fallback;
  return raw ?? fallback;
}

async function invalidateAdminChangeRequestCaches(id: string) {
  await mutate(
    (key) =>
      typeof key === "string" &&
      (key.startsWith("admin-change-requests") ||
        key.startsWith("my-change-requests") ||
        key === `admin-change-request-details-${id}` ||
        key === `change-request-details-${id}`),
  );
}

// The DTO is passed in at submit time (not via a closure-captured factory) so
// callers don't risk sending a stale `reviewNote` from React state that hasn't
// flushed yet.
export function useReviewChangeRequest(id: string) {
  const [isLoading, setIsLoading] = useState(false);

  async function submit(dto: ReviewChangeRequestDto): Promise<ChangeRequestDto | null> {
    setIsLoading(true);
    try {
      const response = await adminChangeRequestsService.review(id, dto);
      const data = response.data as ChangeRequestDto;
      const verb =
        data?.status === "APPROVED"
          ? "approved"
          : data?.status === "REJECTED"
          ? "rejected"
          : "sent back for changes";
      showSuccessToast(`Change request ${verb}.`);
      await invalidateAdminChangeRequestCaches(id);
      return data;
    } catch (err) {
      showErrorToast(extractErrorMessage(err, "Failed to submit review."));
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  return { submit, isLoading };
}
